import { useEffect, useRef, useState, type ReactNode } from 'react';
import { usePrepress, CHECK_ITEMS, type PrepJob, type Check, type CheckItem, type PrepStatus } from '../PrepressContext';

const MISSING = ['Specs', 'Art', 'SKU', 'Color', 'Dieline', 'Dimensions'];
const STATUSES: PrepStatus[] = ['intake', 'validating', 'proofing', 'client_review', 'revision', 'approved', 'released', 'on_hold'];
const cycle: Record<Check, Check> = { pending: 'pass', pass: 'fail', fail: 'pending' };

export default function PrepressWorkspace({ job, onClose }: { job: PrepJob; onClose: () => void }) {
  const P = usePrepress();
  const j = P.jobs.find((x) => x.id === job.id) || job;
  const [miss, setMiss] = useState<string[]>([]);
  const [rev, setRev] = useState('');
  const [via, setVia] = useState('Portal');
  const [skuV, setSkuV] = useState(j.sku || '');
  const [specs, setSpecs] = useState('');
  const [die, setDie] = useState('');
  const [pantone, setPantone] = useState(''); const [cmyk, setCmyk] = useState(''); const [lab, setLab] = useState(''); const [density, setDensity] = useState('');
  const [plate, setPlate] = useState(''); const [dieT, setDieT] = useState(''); const [cyl, setCyl] = useState('');
  const [hold, setHold] = useState(''); const [arch, setArch] = useState(''); const [note, setNote] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [onClose]);

  const Section = ({ n, title, children }: { n: number; title: string; children: ReactNode }) => (
    <div className="req-sec"><div className="req-sec-h"><span className="req-sec-n">{n}</span>{title}</div>{children}</div>
  );
  const checkColor = (c: Check) => (c === 'pass' ? 'st-completed' : c === 'fail' ? 'st-blocked' : 'st-new');
  const passCount = CHECK_ITEMS.filter((c) => j.validation[c] === 'pass').length;

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer req-drawer">
        <div className="drawer-head">
          <button className="drawer-close" onClick={onClose}>×</button>
          <div className="drawer-title">{j.id}</div>
          <div className="drawer-tags">
            <span className="tag dp-prepress">{j.clientName}</span>
            <span className={`tag st-${j.status === 'released' ? 'completed' : j.status === 'on_hold' ? 'blocked' : 'in_progress'}`}>{j.status.replace('_', ' ')}</span>
            {j.sku && <span className="mono">{j.sku}</span>}
          </div>
          <p className="client-summary" style={{ marginTop: 10 }}>{j.product} · {j.source}</p>
        </div>

        <div className="drawer-body req-body">
          <Section n={1} title="Artwork Intake">
            <div className="file-chips">{j.files.map((f) => <span className="file-chip" key={f.id}>📎 {f.name}<small style={{ color: 'var(--tx3)', marginLeft: 4 }}>{f.kind}</small></span>)}{j.files.length === 0 && <span className="sub">No files.</span>}</div>
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => fileRef.current?.click()}>⬆ Add file</button>
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => Array.from(e.target.files || []).forEach((f) => P.attachFile(j.id, f.name, 'artwork'))} />
          </Section>

          <Section n={2} title={`Validate Files · ${passCount}/${CHECK_ITEMS.length} pass`}>
            <div className="chip-row">
              {CHECK_ITEMS.map((c) => (
                <button key={c} className={`tag ${checkColor(j.validation[c])}`} style={{ cursor: 'pointer' }} onClick={() => P.validate(j.id, c as CheckItem, cycle[j.validation[c]])}>{c}: {j.validation[c]}</button>
              ))}
            </div>
            <div className="sub" style={{ marginTop: 6 }}>Click a check to cycle pending → pass → fail.</div>
          </Section>

          <Section n={3} title="Request Missing Info">
            <div className="chip-row">{MISSING.map((m) => <button key={m} className={`select-chip${miss.includes(m) ? ' on' : ''}`} onClick={() => setMiss((s) => s.includes(m) ? s.filter((x) => x !== m) : [...s, m])}>{m}</button>)}</div>
            {j.missingInfo.length > 0 && <div className="sub" style={{ marginTop: 6 }}>Outstanding: {j.missingInfo.join(', ')}</div>}
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => { if (miss.length) { P.requestInfo(j.id, miss); setMiss([]); } }}>Request from CSR/client</button>
          </Section>

          <Section n={4} title="Proofs">
            <div className="mini-list">
              {j.proofs.map((p) => (
                <div className="mini-row" key={p.id}>
                  <div className="mr-main"><strong>Proof v{p.version}</strong><div className="mr-sub">{p.status}</div></div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {p.status === 'internal' && <button className="btn small success" onClick={() => P.approveInternal(j.id, p.id)}>Approve</button>}
                    <button className="btn small" onClick={() => P.sendProof(j.id, p.id)}>Send</button>
                  </div>
                </div>
              ))}
              {j.proofs.length === 0 && <div className="sub">No proofs yet.</div>}
            </div>
            <button className="btn small primary" style={{ marginTop: 8 }} onClick={() => P.createProof(j.id)}>Create proof</button>
            <button className="btn small" style={{ marginTop: 8, marginLeft: 8 }} onClick={() => P.compareVersions(j.id)}>Compare versions</button>
            {j.diffReport && <div className="portal-banner ok" style={{ marginTop: 8 }}>{j.diffReport}</div>}
          </Section>

          <Section n={6} title="Log Revision Request">
            <input placeholder="Client/internal change…" value={rev} onChange={(e) => setRev(e.target.value)} />
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => { if (rev) { P.logRevision(j.id, rev); setRev(''); } }}>Log revision</button>
            {j.revisions.length > 0 && <div className="mini-list" style={{ marginTop: 8 }}>{j.revisions.map((r) => <div className="mini-row" key={r.id}><div className="mr-main"><strong>{r.text}</strong><div className="mr-sub">{r.time}</div></div></div>)}</div>}
          </Section>

          <Section n={9} title="Capture Client Approval">
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={via} onChange={(e) => setVia(e.target.value)} style={{ flex: 1 }}><option>Portal</option><option>Email</option><option>Verbal</option></select>
              <button className="btn small primary" onClick={() => P.captureApproval(j.id, via)}>Capture approval</button>
            </div>
            {j.approvals.map((a) => <div className="sub" key={a.id} style={{ marginTop: 6 }}>✓ {a.text}</div>)}
          </Section>

          <Section n={10} title="Blueprint &amp; SKU">
            <button className="btn small primary" onClick={() => P.createBlueprint(j.id)}>Create blueprint</button>
            {j.blueprint && <div className="sub" style={{ margin: '6px 0' }}>Blueprint: <span className="mono">{j.blueprint.id}</span></div>}
            <div className="form-grid" style={{ marginTop: 8 }}>
              <div className="field"><label>SKU</label><input value={skuV} onChange={(e) => setSkuV(e.target.value)} /></div>
              <div className="field"><label>Specs</label><input value={specs} onChange={(e) => setSpecs(e.target.value)} placeholder="structure, gauge…" /></div>
            </div>
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => { if (skuV) P.setSku(j.id, skuV, specs); }}>Save SKU record</button>
          </Section>

          <Section n={12} title="Dieline · Color · Tooling">
            <div className="form-grid">
              <div className="field full"><label>Dieline</label><input value={die} onChange={(e) => setDie(e.target.value)} placeholder="DIE-… file" /></div>
            </div>
            <button className="btn small" style={{ marginTop: 6 }} onClick={() => { if (die) P.linkDieline(j.id, die); }}>Link dieline</button>
            {j.dieline && <span className="sub" style={{ marginLeft: 8 }}>Linked: {j.dieline}</span>}
            <div className="form-grid" style={{ marginTop: 10 }}>
              <div className="field"><label>Pantone</label><input value={pantone} onChange={(e) => setPantone(e.target.value)} /></div>
              <div className="field"><label>CMYK</label><input value={cmyk} onChange={(e) => setCmyk(e.target.value)} /></div>
              <div className="field"><label>Lab</label><input value={lab} onChange={(e) => setLab(e.target.value)} /></div>
              <div className="field"><label>Density</label><input value={density} onChange={(e) => setDensity(e.target.value)} /></div>
            </div>
            <button className="btn small" style={{ marginTop: 6 }} onClick={() => P.setColor(j.id, { pantone, cmyk, lab, density })}>Record color standard</button>
            <div className="form-grid" style={{ marginTop: 10 }}>
              <div className="field"><label>Plate</label><input value={plate} onChange={(e) => setPlate(e.target.value)} /></div>
              <div className="field"><label>Die</label><input value={dieT} onChange={(e) => setDieT(e.target.value)} /></div>
              <div className="field"><label>Cylinder</label><input value={cyl} onChange={(e) => setCyl(e.target.value)} /></div>
            </div>
            <button className="btn small" style={{ marginTop: 6 }} onClick={() => P.setTooling(j.id, { plate, die: dieT, cylinder: cyl })}>Record plate/tooling</button>
          </Section>

          <Section n={15} title="Release / Hold / Archive">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn small primary" disabled={j.status === 'released'} onClick={() => P.release(j.id)}>{j.status === 'released' ? 'Released ✓' : 'Release to production'}</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input placeholder="Hold reason — technical issue…" value={hold} onChange={(e) => setHold(e.target.value)} />
              <button className="btn small danger" onClick={() => { if (hold) { P.placeHold(j.id, hold); setHold(''); } }}>Hold</button>
            </div>
            {j.hold && <div className="portal-banner warn" style={{ marginTop: 8 }}>On hold: {j.hold}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input placeholder="Drive/master link…" value={arch} onChange={(e) => setArch(e.target.value)} />
              <button className="btn small" onClick={() => { if (arch) { P.archive(j.id, arch); setArch(''); } }}>Archive</button>
            </div>
            {j.archive && <div className="sub" style={{ marginTop: 6 }}>Archived: {j.archive}</div>}
            <div style={{ marginTop: 10 }}>
              <select value={j.status} onChange={(e) => P.updateStatus(j.id, e.target.value as PrepStatus)}>{STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</select>
            </div>
          </Section>

          <Section n={18} title="Technical Notes">
            <input placeholder="Note for production/quality…" value={note} onChange={(e) => setNote(e.target.value)} />
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => { if (note) { P.addNote(j.id, note); setNote(''); } }}>Add note</button>
            {j.notes.map((nt) => <div className="sub" key={nt.id} style={{ marginTop: 6 }}>• {nt.text}</div>)}
          </Section>

          <Section n={0} title="History">
            <div className="record">{j.history.map((h) => <div className="record-row" key={h.id}><span className="record-dot" style={{ background: 'var(--ac)' }} /><div className="record-main"><strong>{h.text}</strong></div><span className="record-time">{h.time}</span></div>)}</div>
          </Section>
        </div>
      </aside>
    </>
  );
}
